import { Controller } from "@hotwired/stimulus"
import { post, patch } from "@rails/request.js"

/**
 * Business Verification Controller
 *
 * Manages the UI for inline BaseLayer business verification.
 * The server owns all verification logic — this controller only:
 * - Triggers inline verification (button click or EIN auto-trigger)
 * - Manages loading/error/success UI states
 * - Saves form data before verifying so the server has current field values
 *
 * Form submission ("Next") is NOT intercepted. The server handles
 * verification during step transition via perform_forward_step_action,
 * and business_already_verified? prevents duplicate API calls.
 */
export default class extends Controller {
  static targets = [
    "verifyButton",
    "loadingOverlay",
    "resultsContainer",
    "errorContainer",
    "cancelButton",
    "einInput",
    "requiredField"
  ]

  static values = {
    verifyUrl: String,
    applicationId: String,
    autoVerify: { type: Boolean, default: false }
  }

  static outlets = ["ein-verification"]

  connect() {
    this.isVerifying = false
    this.lastVerifiedEin = null

    if (this.autoVerifyValue && this.hasEinInputTarget) {
      this.setupEinListener()
    }

    if (this.shouldAutoVerify()) {
      this.requestVerification()
    }
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
    }

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
  }

  // -- Verification entry points (all route through requestVerification) --

  verifyBusiness(event) {
    event?.preventDefault()
    this.lastVerifiedEin = null
    this.requestVerification()
  }

  retryVerification(event) {
    event.preventDefault()
    this.hideError()
    this.requestVerification()
  }

  // -- Single verification path --

  async requestVerification() {
    if (this.isVerifying) return
    if (!this.validateRequiredFields()) {
      this.showValidationError()
      return
    }

    const ein = this.currentNormalizedEin()
    if (ein && this.lastVerifiedEin === ein) return

    this.isVerifying = true
    this.showLoading()
    this.abortController = new AbortController()

    try {
      await this.saveFormData()

      const response = await post(this.verifyUrlValue, {
        body: JSON.stringify({ loan_application_id: this.applicationIdValue }),
        contentType: "application/json",
        responseKind: "turbo-stream",
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error(`Verification failed: ${response.statusText}`)

      if (ein) this.lastVerifiedEin = ein
      this.showSuccessFeedback()
    } catch (error) {
      if (error.name !== "AbortError") {
        this.handleVerificationError(error)
      }
    } finally {
      this.isVerifying = false
      this.hideLoading()
    }
  }

  // -- EIN auto-verification --

  setupEinListener() {
    if (!this.hasEinInputTarget) return

    this.einInputTarget.addEventListener("input", () => {
      this.debouncedCheckEin()
    })

    this.einInputTarget.addEventListener("blur", () => {
      this.checkEinAndVerify()
    })
  }

  debouncedCheckEin() {
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => this.checkEinAndVerify(), 1000)
  }

  checkEinAndVerify() {
    if (!this.hasEinInputTarget) return

    const ein = this.currentNormalizedEin()
    if (!ein) return
    if (this.lastVerifiedEin === ein) return
    if (!this.validateRequiredFields()) return

    this.requestVerification()
  }

  // -- Form data persistence --

  async saveFormData() {
    const form = this.element.closest("form")
    if (!form) return

    const formData = new FormData(form)
    formData.set("auto_save", "true")

    await patch(form.action, {
      body: formData,
      responseKind: "html"
    })
  }

  // -- Cancel --

  cancelVerification(event) {
    event.preventDefault()
    if (this.abortController) this.abortController.abort()
    this.hideLoading()
    this.showCancelMessage()
  }

  // -- UI state management --

  showLoading() {
    if (!this.hasLoadingOverlayTarget) return

    this.loadingOverlayTarget.classList.remove("hidden")

    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.disabled = true
    }

    if (this.isMobile()) {
      document.body.style.overflow = "hidden"
    }

    this.announceToScreenReader("Verifying business information")
  }

  hideLoading() {
    if (!this.hasLoadingOverlayTarget) return

    this.loadingOverlayTarget.classList.add("hidden")

    if (this.hasVerifyButtonTarget) {
      this.verifyButtonTarget.disabled = false
    }

    document.body.style.overflow = ""
  }

  handleVerificationError(error) {
    this.hideLoading()

    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.innerHTML = `
        <div class="rounded-lg border-2 border-red-200 bg-red-50 p-4 sm:p-6">
          <div class="flex items-start">
            <div class="shrink-0">
              <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <h3 class="text-sm font-semibold text-red-900 sm:text-base">Verification Failed</h3>
              <p class="mt-2 text-sm text-red-700">${this.getErrorMessage(error)}</p>
              <div class="mt-4 flex flex-col sm:flex-row gap-3">
                <button type="button"
                  data-action="click->business-verification#retryVerification"
                  class="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                  Try Again
                </button>
                <button type="button"
                  data-action="click->business-verification#dismissError"
                  class="inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                  Continue Without Verification
                </button>
              </div>
            </div>
          </div>
        </div>
      `
      this.errorContainerTarget.classList.remove("hidden")
      this.scrollToElement(this.errorContainerTarget)
    }

    this.announceToScreenReader("Verification failed. Please try again.")
  }

  dismissError(event) {
    event.preventDefault()
    this.hideError()
  }

  hideError() {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.classList.add("hidden")
  }

  showSuccessFeedback() {
    this.hideError()
    this.hideValidationError()

    if (this.hasResultsContainerTarget) {
      this.resultsContainerTarget.classList.add("verification-success")
      setTimeout(() => {
        this.resultsContainerTarget.classList.remove("verification-success")
      }, 1000)

      this.scrollToElement(this.resultsContainerTarget)
    }

    this.dispatch("success", { detail: { applicationId: this.applicationIdValue } })
    this.announceToScreenReader("Business verification completed successfully")
  }

  showCancelMessage() {
    if (!this.hasVerifyButtonTarget) return

    const originalText = this.verifyButtonTarget.textContent
    this.verifyButtonTarget.textContent = "Cancelled"
    setTimeout(() => { this.verifyButtonTarget.textContent = originalText }, 2000)
  }

  showValidationError() {
    if (!this.hasErrorContainerTarget) return

    this.errorContainerTarget.innerHTML = `
      <div class="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-4 sm:p-6">
        <div class="flex items-start">
          <div class="shrink-0">
            <svg class="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-semibold text-yellow-800">Complete Required Fields</h3>
            <p class="mt-1 text-sm text-yellow-700">
              Please fill in all required fields before verifying your business:
            </p>
            <ul class="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
              <li>Business Name</li>
              <li>EIN/Tax ID</li>
              <li>Business Address (select from autocomplete suggestions)</li>
            </ul>
          </div>
        </div>
      </div>
    `
    this.errorContainerTarget.classList.remove("hidden")
  }

  hideValidationError() {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.classList.add("hidden")
    this.errorContainerTarget.innerHTML = ""
  }

  // -- Helpers --

  validateRequiredFields() {
    return this.requiredFieldTargets.every(field => field.value.trim())
  }

  currentNormalizedEin() {
    if (!this.hasEinInputTarget) return null

    const ein = this.einInputTarget.value.trim()
    if (!this.isValidEinFormat(ein)) return null

    return ein.replace(/-/g, "")
  }

  isValidEinFormat(ein) {
    if (!ein) return false
    return /^\d{2}-\d{7}$/.test(ein.trim()) || /^\d{9}$/.test(ein.trim())
  }

  shouldAutoVerify() {
    return this.element.dataset.autoVerify === "true"
  }

  isMobile() {
    return window.innerWidth < 768
  }

  scrollToElement(element) {
    if (!element) return

    const offset = this.isMobile() ? 80 : 120
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset

    window.scrollTo({ top: elementPosition - offset, behavior: "smooth" })
  }

  announceToScreenReader(message) {
    const announcement = document.createElement("div")
    announcement.setAttribute("role", "status")
    announcement.setAttribute("aria-live", "polite")
    announcement.className = "sr-only"
    announcement.textContent = message
    document.body.appendChild(announcement)
    setTimeout(() => announcement.remove(), 1000)
  }

  getErrorMessage(error) {
    if (error.message.includes("Network")) {
      return "Unable to connect to verification service. Please check your internet connection and try again."
    } else if (error.message.includes("timeout")) {
      return "Verification is taking longer than expected. Please try again."
    } else {
      return "We encountered an issue verifying your business. You can continue without verification or try again."
    }
  }
}
